import {
  SCHEMA_VERSION,
  type CampaignDraft,
  type CampaignDraftData,
} from "src/services/CampaignDraftTypes";
import {
  readString,
  readStringList,
  toCurrentStep,
  toStepIndex,
} from "../../../shared/CampaignDraftUtils";
import { resolveCampaignType, resolveSubCampaign, isCustomCampaignType } from "./AiCampaignUtils";
import { EMAIL_WIZARD_STEPS, createInitialAiCampaignForm } from "./AiCampaignConstants";
import type { AiCampaignForm } from "./AiCampaignTypes";

interface AiDraftState {
  stepIndex: number;
  form: AiCampaignForm;
  sendTo: string[];
  excludeList: string[];
  replyTo: string;
}

export interface RestoredAiDraft extends AiDraftState {
  hasTemplate: boolean;
}

const isAiCampaignForm = (value: unknown): value is AiCampaignForm =>
  typeof value === "object" &&
  value !== null &&
  "selectedCategories" in value &&
  Array.isArray(value.selectedCategories) &&
  "campaignName" in value &&
  typeof value.campaignName === "string" &&
  "prompt" in value &&
  typeof value.prompt === "string" &&
  "subjectLine" in value &&
  typeof value.subjectLine === "string";

/**
 * The known fields mirror the generate and send APIs so the campaigns list can show them;
 * `form` keeps the exact selections (custom types, tone) so a resumed wizard looks as it was left.
 */
export const buildAiDraftData = (
  { stepIndex, form, sendTo, excludeList, replyTo }: AiDraftState,
  toneLabels: string[]
): CampaignDraftData => ({
  schema_version: SCHEMA_VERSION,
  wizard: "ai",
  current_step: toCurrentStep(stepIndex),
  campaign_name: form.campaignName.trim(),
  send_to: sendTo,
  dont_send_to: excludeList,
  category_links: form.selectedCategories,
  campaign_type: resolveCampaignType(form),
  campaign_subtype: resolveSubCampaign(form),
  is_custom_category: isCustomCampaignType(form),
  prompt: form.prompt.trim(),
  tone: toneLabels,
  persona: form.persona.trim(),
  seasonal_tag: form.seasonalTag.trim(),
  offer: form.offer.trim(),
  cta_label: form.ctaLabel.trim(),
  cta_url: form.ctaUrl.trim(),
  subject_line: form.subjectLine.trim(),
  reply_to: replyTo.trim(),
  form,
});

export const restoreAiDraft = (draft: CampaignDraft): RestoredAiDraft => {
  const { data, content } = draft;
  const form = isAiCampaignForm(data.form)
    ? data.form
    : createInitialAiCampaignForm(readString(data.campaign_name));

  return {
    stepIndex: toStepIndex(data, EMAIL_WIZARD_STEPS.length),
    form,
    sendTo: readStringList(data.send_to),
    excludeList: readStringList(data.dont_send_to),
    replyTo: readString(data.reply_to),
    hasTemplate: Boolean(content?.design || content?.html),
  };
};
